"use client"

import { Button } from "@/shared/components/ui/button"
import { Badge } from "@/shared/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { X, MapPin, Calendar, CheckCircle, XCircle, Phone } from "lucide-react"
import type { TravelPackage, ActivityFormat } from "@/shared/types/packages"
import { Link } from "react-router-dom"

// Helper function to get category-specific badge styles
const getCategoryBadgeStyle = (category: string) => {
  const lowerCategory = category.toLowerCase()
  
  switch (lowerCategory) {
    case 'cruise':
      return 'bg-orange-600 text-white px-3 py-1.5 text-xs font-semibold shadow-md shrink-0 border-0'
    case 'international':
      return 'bg-orange-500 text-white px-3 py-1.5 text-xs font-semibold shadow-md shrink-0 border-0'
    case 'domestic':
      return 'bg-orange-400 text-white px-3 py-1.5 text-xs font-semibold shadow-md shrink-0 border-0'
    default:
      return 'bg-gray-800 text-white px-3 py-1.5 text-xs font-semibold shadow-md shrink-0 border-0'
  }
}

type ParsedActivity = {
  mainActivity: string
  subActivities: string[]
}

const isDomesticPackage = (category: string) => category.toLowerCase() === 'domestic'

const normalizeText = (value: string): string => value.trim().replace(/\s+/g, ' ')

const parseActivity = (activity: ActivityFormat): ParsedActivity => {
  if (typeof activity === 'object' && activity && 'activity' in activity) {
    return {
      mainActivity: normalizeText(activity.activity || ''),
      subActivities: (activity.subActivities || []).map((sub) => normalizeText(String(sub))).filter(Boolean),
    }
  }

  const raw = normalizeText(String(activity || ''))
  if (!raw) return { mainActivity: '', subActivities: [] }

  const parts = raw.split(/\s*(?:->|→)\s*/).map(normalizeText).filter(Boolean)
  if (parts.length === 0) return { mainActivity: '', subActivities: [] }

  return {
    mainActivity: parts[0],
    subActivities: parts.slice(1),
  }
}

const dedupeDayActivities = (activities: ActivityFormat[] = []): ParsedActivity[] => {
  const uniqueByMain = new Map<string, ParsedActivity>()

  activities.forEach((activity) => {
    const parsed = parseActivity(activity)
    if (!parsed.mainActivity) return

    const key = parsed.mainActivity.toLowerCase()
    const existing = uniqueByMain.get(key)

    if (!existing) {
      uniqueByMain.set(key, {
        mainActivity: parsed.mainActivity,
        subActivities: [...parsed.subActivities],
      })
      return
    }

    existing.subActivities = [...existing.subActivities, ...parsed.subActivities]
  })

  return Array.from(uniqueByMain.values()).map((activity) => {
    const seenSubs = new Set<string>()
    const mainKey = activity.mainActivity.toLowerCase()
    const filteredSubs = activity.subActivities.filter((sub) => {
      const normalized = normalizeText(sub)
      if (!normalized) return false
      const subKey = normalized.toLowerCase()
      if (subKey === mainKey || seenSubs.has(subKey)) return false
      seenSubs.add(subKey)
      return true
    })

    return {
      mainActivity: activity.mainActivity,
      subActivities: filteredSubs,
    }
  })
}

interface PackageDetailsModalProps {
  package: TravelPackage
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function PackageDetailsModal({ package: pkg, isOpen, onOpenChange }: PackageDetailsModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="dialog-max-w-7xl w-[95vw] max-w-[80rem] sm:min-w-[600px] md:min-w-[700px] lg:min-w-[800px] max-h-[90vh] flex flex-col bg-white p-0"
        style={{ maxWidth: '80rem', width: '95vw' }}
      >
        <DialogHeader className="border-b bg-white relative shrink-0 px-4 py-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 hover:bg-gray-100 rounded-full z-50 h-8 w-8"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="pr-10">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-4">
                <DialogTitle className="text-lg font-bold text-balance leading-tight flex-1">
                  {pkg.title}
                </DialogTitle>
                <Badge className={getCategoryBadgeStyle(pkg.category)}>
                  {pkg.category}
                </Badge>
              </div>

              <DialogDescription className="flex flex-wrap items-center gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                  <MapPin className="h-3 w-3 text-orange-500" />
                  {pkg.location}
                </span>
                <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                  <Calendar className="h-3 w-3 text-orange-500" />
                  {pkg.days} Days / {pkg.nights} Nights
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto p-4 bg-gradient-to-b from-slate-50 to-white flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h2 className="text-xl font-bold text-slate-900">{pkg.title}</h2>
                  <Badge className={getCategoryBadgeStyle(pkg.category)}>{pkg.category}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-700 mb-3">
                  <span className="flex items-center gap-1.5 font-medium">
                    <MapPin className="h-3 w-3 text-orange-500" />
                    {pkg.location}
                  </span>
                  <span className="flex items-center gap-1.5 font-medium">
                    <Calendar className="h-3 w-3 text-orange-500" />
                    {pkg.days} Days / {pkg.nights} Nights
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-slate-700">{pkg.overview || 'No package overview available.'}</p>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 min-h-[26rem]">
                <h3 className="text-lg font-bold mb-4">Itinerary</h3>
                <div className="space-y-5">
                  {pkg.itinerary && pkg.itinerary.length > 0 ? (
                    pkg.itinerary.map((day) => {
                      const dayActivities = dedupeDayActivities(day.activities || [])

                      return (
                        <div key={`${day.day}-${day.title}`} className="relative pl-8 border-l-2 border-orange-200 last:border-l-0 pb-4 last:pb-0">
                          <div className="absolute -left-4 top-0 w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-xs border-2 border-white">
                            {day.day}
                          </div>

                          <h4 className="text-base font-bold mb-2 text-slate-900">{day.title || `Day ${day.day}`}</h4>

                          {dayActivities.length > 0 ? (
                            <ul className="space-y-2">
                              {dayActivities.map((activity, idx) => (
                                <li key={`${activity.mainActivity}-${idx}`} className="text-slate-700">
                                  <div className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                                    <div className="flex-1">
                                      <span className="text-xs leading-relaxed font-medium">{activity.mainActivity}</span>
                                      {activity.subActivities.length > 0 && (
                                        <ul className="mt-1 ml-3 space-y-0.5">
                                          {activity.subActivities.map((subActivity, subIdx) => (
                                            <li key={`${subActivity}-${subIdx}`} className="flex items-start gap-1.5">
                                              <span className="w-1 h-1 rounded-full bg-orange-300 mt-1 shrink-0" />
                                              <span className="text-xs leading-relaxed text-slate-600">{subActivity}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-gray-500 italic">No activities listed</p>
                          )}

                          {day.meals && day.meals.length > 0 && (
                            <div className="mt-3 bg-gradient-to-r from-slate-50 to-orange-50/30 rounded px-3 py-2 border border-slate-200">
                              <p className="text-xs text-slate-600">
                                <span className="font-semibold text-slate-900">Meals:</span> {day.meals.join(', ')}
                              </p>
                            </div>
                          )}

                          {/* accommodation field was previously not shown on client side */}
                          {day.accommodation && (
                            <div className="mt-2 text-sm text-slate-600">
                              <span className="font-semibold text-slate-900">Accommodation:</span> {day.accommodation}
                            </div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <p className="text-gray-500 italic">No itinerary details available</p>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-slate-200">
                <img
                  src={pkg.image || "/logo.svg"}
                  alt={pkg.title}
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-base font-bold mb-3">Package Highlights</h3>
                <div className="flex flex-wrap gap-2">
                  {(pkg.packageHighlights || []).length > 0 ? (
                    pkg.packageHighlights.map((highlight, index) => (
                      <Badge
                        key={index}
                        className="bg-gradient-to-r from-orange-100 to-orange-50 text-orange-700 border-0 px-3 py-1 text-xs font-medium rounded-full"
                      >
                        {highlight}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 italic">No highlights available</p>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center">
                    <CheckCircle className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold mb-2 flex items-center gap-2">
                      Visa Information
                      {!pkg.visaRequired && !isDomesticPackage(pkg.category) && (
                        <Badge className="bg-green-500 text-white text-xs px-2 py-0.5">Visa Free</Badge>
                      )}
                    </h3>
                    <p className="text-xs leading-relaxed text-slate-700">
                      {pkg.visaInfo || (isDomesticPackage(pkg.category) ? 'Visa information is not applicable for domestic packages.' : (pkg.visaRequired ? 'Visa is required for this package.' : 'No visa required for this package.'))}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                  <h3 className="text-base font-bold">Inclusion</h3>
                </div>
                <ul className="space-y-2">
                  {(pkg.inclusions || []).length > 0 ? (
                    pkg.inclusions.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle className="h-3 w-3 text-green-600 shrink-0 mt-0.5" />
                        <span className="text-xs leading-relaxed">{item}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-xs text-gray-500 italic">No inclusions available</li>
                  )}
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                    <XCircle className="h-3 w-3 text-white" />
                  </div>
                  <h3 className="text-base font-bold">Exclusion</h3>
                </div>
                <ul className="space-y-2">
                  {(pkg.exclusions || []).length > 0 ? (
                    pkg.exclusions.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-700">
                        <XCircle className="h-3 w-3 text-red-500 shrink-0 mt-0.5" />
                        <span className="text-xs leading-relaxed">{item}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-xs text-gray-500 italic">No exclusions available</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t bg-gradient-to-r from-white via-orange-50/30 to-white p-3 shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="text-center sm:text-left">
                <span className="text-xs text-slate-500 font-medium block mb-1">Starting from</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-orange-500">
                    {pkg.currency === 'PHP' ? '₱' : '$'}{pkg.price.toLocaleString()}
                  </span>
                  <span className="text-sm text-slate-600 font-medium">/ person</span>
                </div>
              </div>
              
              <div className="hidden sm:block w-px h-8 bg-slate-200"></div>
              
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border border-orange-500 text-orange-500 hover:bg-orange-50 bg-transparent font-semibold px-4 py-2 text-sm rounded"
                asChild
              >
                <Link to="/contact" className="flex items-center justify-center gap-1">
                  <Phone className="h-3 w-3" />
                  Contact
                </Link>
              </Button>
              <Button
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 text-sm rounded"
                asChild
              >
                <Link to="/contact">
                  Book Now →
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
